import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  tagDetailSchema,
  tagOptionSchema,
  tagSchema,
  type ListTagsParams,
  type Tag,
  type TagDetail,
  type TagFormValues,
  type TagOption,
} from './schema';

const BASE_PATH = '/tags';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(tagSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: tagDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: tagSchema.nullable(),
  error: z.string().nullable().optional(),
});

const optionsResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: z.array(tagOptionSchema).nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

// Drop blank rows; trim names.
function toWireTranslations(values: TagFormValues) {
  return values.translations
    .filter((t) => t.name.trim().length > 0)
    .map((t) => ({ localeCode: t.localeCode, name: t.name.trim() }));
}

function toCreatePayload(values: TagFormValues) {
  return {
    code: values.code,
    name: values.name,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

// `code` is intentionally absent — immutable post-create (server forbids non-whitelisted).
function toUpdatePayload(values: TagFormValues) {
  return {
    name: values.name,
    isActive: values.isActive,
    translations: toWireTranslations(values),
  };
}

export async function listTags(
  params: ListTagsParams,
): Promise<CommonResponse<Tag>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getTag(id: string): Promise<TagDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function getTagOptions(): Promise<TagOption[]> {
  const response = await apiClient.get(`${BASE_PATH}/options`);
  const parsed = optionsResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createTag(values: TagFormValues): Promise<Tag> {
  const response = await apiClient.post<ApiResponseShape<Tag>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateTag(
  id: string,
  values: TagFormValues,
): Promise<Tag> {
  const response = await apiClient.put<ApiResponseShape<Tag>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}
